<template>
  <div class="tags-trigger button-light">
    <button class="tags-trigger__main" type="button" @click="emit('click')">
      <span class="tags-trigger__label">Tag:</span>
      <span v-if="hasTag" class="tags-trigger__value">{{ normalizedTag }}</span>
      <span v-else class="tags-trigger__value tags-trigger__value--empty">No Tags!</span>
    </button>

    <v-menu
      v-model="showTagMenu"
      :location="'bottom end'"
      :close-on-content-click="true"
    >
      <template #activator="{ props }">
        <button
          class="tags-trigger__chevron"
          type="button"
          v-bind="props"
          @click.stop
        >
          <SVGIcon
            :iconName="'expand_more'"
            :width="'18px'"
            :height="'18px'"
          />
        </button>
      </template>

      <v-list density="compact">
        <v-list-item @click="openInfoDialog">
          <v-list-item-title>Info</v-list-item-title>
        </v-list-item>
        <v-list-item @click="openCreateTagDialog">
          <v-list-item-title>Tag This Version</v-list-item-title>
        </v-list-item>
        <v-list-item @click="openRestoreDialog">
          <v-list-item-title>Restore a Tag</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-menu>

    <v-dialog v-model="showInfoDialog" max-width="560">
      <v-card class="tag-info-dialog">
        <v-card-title class="text-h6 tag-info-dialog__title">Tag Information</v-card-title>
        <v-card-text>
          <div v-if="infoRows.length" class="tag-info-list">
            <div v-for="row in infoRows" :key="row.label" class="statistics-field">
              <div class="description">{{ row.label }}</div>
              <div class="value">{{ row.value }}</div>
            </div>
          </div>
          <div v-else class="tag-info-empty">No tag information available.</div>
        </v-card-text>
        <v-card-actions class="justify-end tag-info-dialog__actions">
          <v-btn class="button-light" variant="flat" @click="showInfoDialog = false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <TagRestore
      v-model="showRestoreDialog"
      :datasetId="datasetId"
      :tags="tagList"
      :loading="tagListLoading"
      @restored="handleTagRestored"
    />

    <TagCreateDialog
      v-model:show="showCreateTagDialog"
      :dataset-id="datasetId"
      @created="handleTagCreated"
    />
  </div>
</template>

<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import SVGIcon from '@/components/SVGIcon.vue';
import TagRestore from '@/components/DatasetComponent/DatasetManagement/DatasetTags/TagRestore.vue';
import TagCreateDialog from '@/components/DatasetComponent/DatasetManagement/DatasetTags/TagCreateDialog.vue';
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';

const props = defineProps({
  datasetId: {
    type: [String, Number],
    default: ''
  },
  currentTag: {
    type: String,
    default: ''
  },
  tagInfo: {
    type: Object,
    default: () => ({})
  }
});

const emit = defineEmits(['click', 'action']);
const showTagMenu = ref(false);
const showInfoDialog = ref(false);
const showRestoreDialog = ref(false);
const showCreateTagDialog = ref(false);
const tagList = ref([]);
const tagListLoading = ref(false);

const normalizedTag = computed(() => (props.currentTag || '').trim());
const hasTag = computed(() => normalizedTag.value.length > 0);
const selectedTagRecord = computed(() => {
  if (!Array.isArray(tagList.value) || !tagList.value.length) {
    return null;
  }

  const current = normalizedTag.value.toLowerCase();
  if (!current) {
    return tagList.value[0];
  }

  const match = tagList.value.find((item) => {
    const candidate = (item?.name || item?.tag || item?.tad || '').toString().toLowerCase();
    return candidate === current;
  });

  return match || tagList.value[0];
});

const safeValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return '-';
  }
  return String(value);
};

const formatDate = (value) => {
  if (!value) {
    return '-';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return safeValue(value);
  }
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const infoRows = computed(() => {
  const info = props.tagInfo || {};
  const record = selectedTagRecord.value || {};

  return [
    {
      label: 'Tag',
      value: safeValue(normalizedTag.value || record.name || record.tag || record.tad)
    },
    {
      label: 'Description',
      value: safeValue(info.tag_description || info.tagDescription || record.description || record.tag_description)
    },
    {
      label: 'User',
      value: safeValue(info.tag_user || info.user || record.user || record.created_by || record.owner)
    },
    {
      label: 'Date',
      value: formatDate(info.tag_date || info.date || record.created_at || record.date || record.createdAt)
    },
    {
      label: 'Serial',
      value: safeValue(info.serial || info.tag_serial || record.serial || record.serial_number || record.version_serial || record.id)
    }
  ];
});

const openInfoDialog = () => {
  showTagMenu.value = false;
  showInfoDialog.value = true;
};

const openRestoreDialog = () => {
  showTagMenu.value = false;
  showRestoreDialog.value = true;
};

const openCreateTagDialog = async () => {
  showTagMenu.value = false;
  await nextTick();
  showCreateTagDialog.value = true;
};

const handleTagCreated = async () => {
  await loadTagList();
  emit('action', 'tag-this-version');
};

const handleTagRestored = async (restoredTagName) => {
  await loadTagList();
  emit('action', { type: 'restore-tag', tagName: restoredTagName });
};

const loadTagList = async () => {
  if (!props.datasetId) {
    tagList.value = [];
    return;
  }

  tagListLoading.value = true;
  try {
    const dsConn = new DatastoreConnect();
    const response = await dsConn.api('version.tag.list', { dataset_id: props.datasetId });
    tagList.value = Array.isArray(response?.result) ? response.result : [];
  } catch (error) {
    console.error('Error loading tag list:', error);
    tagList.value = [];
  } finally {
    tagListLoading.value = false;
  }
};

watch(() => props.datasetId, () => {
  loadTagList();
}, { immediate: true });
</script>

<style lang="scss" scoped>
.tag-info-dialog {
  @include themify() {
    background: themed('content-background');
    color: themed('body-text-color');
  }

  &__title {
    border-bottom: 1px solid rgba(var(--color-primary-rgb), 0.25);
    color: var(--color-primary);
    font-weight: 700;
  }

  &__actions {
    border-top: 1px solid rgba(var(--color-primary-rgb), 0.15);
  }
}

.tags-trigger {
  display: inline-flex;
  align-items: stretch;
  gap: 0;
  padding: 0;

  &__main,
  &__chevron {
    border: none;
    background: transparent;
    color: inherit;
    font: inherit;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 100%;
  }

  &__main {
    padding: 6px 10px 6px 12px;
    gap: 4px;
  }

  &__chevron {
    padding: 6px 8px 6px 6px;
    border-left: 1px solid rgba(0, 0, 0, 0.12);
  }

  &__label,
  &__value {
    line-height: 1;
  }

  &__value--empty {
    color: #d32f2f;
    font-weight: 600;
  }
}

.tag-info-list {
  display: block;

  .statistics-field {
    display: flex;
    flex-direction: row;
    gap: 10px;
    margin-top: 1px;
    padding: 6px 8px;
    border-radius: 6px;
  }

  .description {
    font-size: 0.8rem;
    font-weight: 600;
    color: var(--body-text-color);
    width: 120px;
    min-width: 120px;
  }

  .value {
    font-size: 0.8rem;
    font-weight: 600;
    color: var(--body-text-color-secondary);
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
}

.tag-info-empty {
  color: var(--body-text-color-secondary, #666);
}
</style>
